import { useEffect, type RefObject } from 'react';

import type Quill from 'quill';

interface UseLinkTooltipProps {
  containerRef: RefObject<HTMLDivElement>;
  quill: Quill | null;
  offset?: number;
}

export const useLinkTooltip = ({
  containerRef,
  quill,
  offset = 10,
}: UseLinkTooltipProps) => {
  useEffect(() => {
    if (!containerRef.current || !quill) return;

    let lastSelection: { index: number; length: number } | null = null;
    const handleSelectionChange = (
      range: { index: number; length: number } | null,
    ) => {
      if (range) lastSelection = range;
    };
    quill.on('selection-change', handleSelectionChange);

    const adjustTooltipPosition = () => {
      const tooltip = containerRef.current?.querySelector(
        '.ql-tooltip',
      ) as HTMLElement;
      if (tooltip && tooltip.style.display !== 'none') {
        const editor = containerRef.current?.querySelector(
          '.ql-editor',
        ) as HTMLElement;
        if (editor) {
          const selection = quill.getSelection(false) || lastSelection;
          if (selection) {
            const bounds = quill.getBounds(selection.index, selection.length);
            if (bounds) {
              const editorRect = editor.getBoundingClientRect();
              const tooltipRect = tooltip.getBoundingClientRect();
              const viewportWidth = window.innerWidth;
              const viewportHeight = window.innerHeight;

              let top = editorRect.top + bounds.bottom + offset;
              let left = editorRect.left + bounds.left;

              const maxRight = viewportWidth - 8;
              const expectedRight = left + tooltipRect.width;
              if (expectedRight > maxRight) {
                left -= expectedRight - maxRight;
              }

              if (left < 8) left = 8;

              const maxBottom = viewportHeight - 8;
              const expectedBottom = top + tooltipRect.height;
              if (expectedBottom > maxBottom) {
                top = editorRect.top + bounds.top - tooltipRect.height - offset;
              }

              tooltip.style.top = `${top}px`;
              tooltip.style.left = `${left}px`;
              tooltip.style.position = 'fixed';
            }
          }
        }
      }
    };

    const observer = new MutationObserver(() => {
      if (!containerRef.current) return;
      const tooltip = containerRef.current?.querySelector(
        '.ql-tooltip',
      ) as HTMLElement;
      if (tooltip && tooltip.style.display !== 'none') {
        adjustTooltipPosition();
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      quill.off('selection-change', handleSelectionChange);
      observer.disconnect();
    };
  }, [containerRef, quill, offset]);
};
